import React, { ComponentProps, useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import { z } from 'zod';
import {
    useCreateParkingSharingAvailability,
    useCreateParkingSharingRequest,
    useEditParkingSharingAvailability,
    useEditParkingSharingRequest,
    useGetParkingSharingAvailabilities,
    useGetParkingSharingAvailability,
    useGetParkingSharingRequest,
} from '_api/useParkingSharing';
import { useOwnProfile } from '_api/useProfile';
import { SafeAreaView } from '_dependencies/safeArea';
import { useAppNavigation } from '_navigator';
import { setShowParkingFeedbackModalAfter } from '_redux/modals';
import { addSpot } from '_redux/parkingSpot';
import {
    isBookingInSlot,
    isWeb,
    showToast,
    useAppDispatch,
    useConfirmDiscard,
    useGlobalState,
    useKeyboardIsVisible,
    WW,
} from '_utils';
import { track } from '_utils/Amplitude';
import handleInvalidSubmit from '_utils/handleInvalidSubmit';
import { useSelectedCoopItem } from '_utils/hooks';
import { flattenIniniteResult } from '_utils/misc';
import safeParse from '_utils/safeParse';
import { addBreadcrumb } from '_utils/Sentry';
import { screenMargin, smallestMargin } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import useTimeRangeSettings from '_utils/useTimeRangeSettings';
import {
    HeaderWithNav,
    HeimeText,
    PrimaryButton,
    UserListItem,
    FormDateTimeInput,
    FormInput,
    Icon,
    CooperativeGroupDropdown,
    Loader,
} from 'Components';
import { BookingSharingAvailability, BookingSharingRequest } from 'types/BookingSharing';
import { findInitialCooperativeGroupId } from 'types/Cooperative';
import { RoutePropsSchema } from 'types/Utility';
import BookingSharingDetails from '../common/BookingSharingDetails';
import BookingSharingTag from '../common/BookingSharingTag';
import getAvailabilityNonTakenSlots from '../common/getAvailabilityNonTakenSlots';
import useGetAssignedParkingAvailability from '../common/useGetAssignedParkingAvailability';

const BookingSharingNewPropsSchema = RoutePropsSchema(
    z.union([
        z.object({
            variant: z.literal('availability'),
            requestId: z.string(),
        }),
        z.object({
            variant: z.literal('availability'),
            availabilityToEdit: z.string().nullable(),
        }),
        z.object({
            variant: z.literal('request'),
            availabilityId: z.string(),
        }),
        z.object({
            variant: z.literal('request'),
            requestToEdit: z.string().nullable(),
        }),
    ]),
);

const getSchema = (requireContent: boolean) =>
    z.object({
        content: requireContent ? z.string().min(1) : z.string(),
        start_at: z.date(),
        end_at: z.date(),
        cooperative_group_id: z.number().nullable(),
        request_for_approval_id: z.string().nullable(),
        requested_availability_slot_id: z.string().nullable(),
    });
type FormValues = z.infer<ReturnType<typeof getSchema>>;

const BookingSharingNew = (props: z.infer<typeof BookingSharingNewPropsSchema>) => {
    const params = safeParse(props, BookingSharingNewPropsSchema).route.params;
    const { data: respondingToRequest, status: respondingToRequestStatus } = useGetParkingSharingRequest(
        'requestId' in params ? params.requestId : '',
    );
    const { data: requestToEdit, status: requestToEditStatus } = useGetParkingSharingRequest(
        'requestToEdit' in params && params.requestToEdit ? params.requestToEdit : '',
    );
    const { data: respondingToAvailability, status: respondingToAvailabilityStatus } = useGetParkingSharingAvailability(
        'availabilityId' in params ? params.availabilityId : '',
    );
    const { data: availabilityToEdit, status: availabilityToEditStatus } = useGetParkingSharingAvailability(
        'availabilityToEdit' in params && params.availabilityToEdit ? params.availabilityToEdit : '',
    );

    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(setShowParkingFeedbackModalAfter(moment().add({ hour: 1 }).unix()));
    }, [dispatch]);

    const validateNotDeleted = function <T>(data: T | undefined | null): data is Exclude<T, { status: 'deleted' }> {
        if (!data || typeof data !== 'object') {
            return false;
        }
        if ('status' in data && data.status === 'deleted') {
            return false;
        }
        return true;
    };
    const assignedRequests = validateNotDeleted(availabilityToEdit)
        ? availabilityToEdit.assigned_requests
        : validateNotDeleted(respondingToAvailability)
          ? respondingToAvailability.assigned_requests
          : [];

    const validateOrThrow = function <T>(value: T) {
        if (value && validateNotDeleted(value)) {
            return value;
        } else {
            addBreadcrumb('debugInfo', JSON.stringify(value));
            throw new Error('Value is not loaded / deleted');
        }
    };

    if (params.variant === 'request') {
        if ('requestToEdit' in params && params.requestToEdit) {
            if (requestToEditStatus === 'pending') {
                return <Loader bgColor="mainBackground" />;
            }
            return (
                <BookingSharingNewDataLoaded variant="edit-request" editableRequest={validateOrThrow(requestToEdit)} />
            );
        } else if ('availabilityId' in params && params.availabilityId) {
            if (respondingToAvailabilityStatus === 'pending') {
                return <Loader bgColor="mainBackground" />;
            }
            return (
                <BookingSharingNewDataLoaded
                    variant="new-request-from-availability"
                    respondingToAvailability={validateOrThrow(respondingToAvailability)}
                    assignedRequests={assignedRequests}
                />
            );
        } else {
            return <BookingSharingNewDataLoaded variant="new-request" />;
        }
    } else {
        if ('availabilityToEdit' in params && params.availabilityToEdit) {
            if (availabilityToEditStatus === 'pending') {
                return <Loader bgColor="mainBackground" />;
            }
            return (
                <BookingSharingNewDataLoaded
                    variant="edit-availability"
                    editableAvailability={validateOrThrow(availabilityToEdit)}
                    assignedRequests={assignedRequests}
                />
            );
        } else if ('requestId' in params && params.requestId) {
            if (respondingToRequestStatus === 'pending') {
                return <Loader bgColor="mainBackground" />;
            }
            return (
                <BookingSharingNewDataLoaded
                    variant="new-availability-from-request"
                    respondingToRequest={validateOrThrow(respondingToRequest)}
                />
            );
        } else {
            return <BookingSharingNewDataLoaded variant="new-availability" />;
        }
    }
};

type DataVariants =
    | {
          // Creating request based on nothing
          variant: 'new-request';
      }
    | {
          // Creating request based on availability
          variant: 'new-request-from-availability';
          respondingToAvailability: BookingSharingAvailability;
          assignedRequests: BookingSharingRequest[];
      }
    | {
          // Creating availability based on nothing
          variant: 'new-availability';
      }
    | {
          // Creating availability based on request
          variant: 'new-availability-from-request';
          respondingToRequest: BookingSharingRequest;
      }
    | {
          // Editing request based on nothing
          variant: 'edit-request';
          editableRequest: BookingSharingRequest;
      }
    | {
          // Editing availability
          variant: 'edit-availability';
          editableAvailability: BookingSharingAvailability;
          assignedRequests: BookingSharingRequest[];
      };

const getDefaultValues = (
    props: DataVariants,
    defaultContent: string | null,
    cooperative_group_id: number | null,
): FormValues => {
    const defaultData: FormValues = {
        content: '',
        start_at: new Date(),
        end_at: moment().add(1, 'hour').toDate(),
        cooperative_group_id,
        request_for_approval_id: null,
        requested_availability_slot_id: null,
    };
    switch (props.variant) {
        case 'new-availability':
            return {
                ...defaultData,
                content: defaultContent ?? '',
            };
        case 'new-request':
            return defaultData;
        case 'new-request-from-availability':
            const availableSlots = getAvailabilityNonTakenSlots(props.respondingToAvailability, props.assignedRequests);
            if (availableSlots.length === 0) {
                return defaultData;
            }
            const firstAvailableSlot = availableSlots[0];
            return {
                ...defaultData,
                start_at: new Date(firstAvailableSlot.start),
                end_at: new Date(firstAvailableSlot.end),
                requested_availability_slot_id: props.respondingToAvailability.id,
            };
        case 'new-availability-from-request':
            return {
                ...defaultData,
                start_at: new Date(props.respondingToRequest.start_at),
                end_at: new Date(props.respondingToRequest.end_at),
                request_for_approval_id: props.respondingToRequest.id,
                content: defaultContent ?? '',
            };
        case 'edit-request':
            return {
                ...defaultData,
                ...props.editableRequest,
                start_at: new Date(props.editableRequest.start_at),
                end_at: new Date(props.editableRequest.end_at),
            };
        case 'edit-availability':
            return {
                ...defaultData,
                ...props.editableAvailability,
                start_at: new Date(props.editableAvailability.start_at),
                end_at: new Date(props.editableAvailability.end_at),
            };
    }
};

const getIsCreatingNew = (variant: DataVariants['variant']) =>
    variant === 'new-request' ||
    variant === 'new-availability' ||
    variant === 'new-request-from-availability' ||
    variant === 'new-availability-from-request';

const getObjectType = (variant: DataVariants['variant']) =>
    variant === 'edit-availability' || variant === 'new-availability-from-request' || variant === 'new-availability'
        ? 'availability'
        : 'request';

const BookingSharingNewDataLoaded = (props: DataVariants) => {
    const { navigate, goBack: internalGoBack } = useAppNavigation();
    const { t } = useTranslation();
    const cooperativeGroups = useSelectedCoopItem()?.cooperative_groups ?? [];
    const parkingSpots = useGlobalState((state) => state.parkingSpot.parkingSpots);
    const appDispatch = useAppDispatch();

    const objectType = getObjectType(props.variant);
    const formValues = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: getDefaultValues(
            props,
            parkingSpots[0]?.label ?? null,
            findInitialCooperativeGroupId(cooperativeGroups),
        ),
        resolver: zodResolver(getSchema(objectType === 'availability')),
    });

    const { theme } = useThemeContext();
    const { mutate: createRequest, isPending: isCreatingRequest } = useCreateParkingSharingRequest();
    const { mutate: updateRequest, isPending: isUpdatingRequest } = useEditParkingSharingRequest();
    const { mutate: createAvailability, isPending: isCreatingAvailability } = useCreateParkingSharingAvailability();
    const { mutate: updateAvailability, isPending: isUpdatingAvailability } = useEditParkingSharingAvailability();

    const isCreatingNew = getIsCreatingNew(props.variant);
    const { content } = useConfirmDiscard(formValues.formState.isDirty, !isCreatingNew, internalGoBack);

    const handleSubmit = (values: FormValues) => {
        const onError = () => {
            showToast({
                header: t('bookingSharing:new_error'),
                text: '',
                type: 'error',
                position: 'bottom',
            });
        };
        const data = {
            ...values,
            start_at: values.start_at.toISOString(),
            end_at: values.end_at.toISOString(),
        };
        const getOnSuccess = (params: { type: 'availability' | 'request'; id: string }) => {
            if (params.type === 'availability') {
                appDispatch(addSpot({ label: values.content }));
            }
            formValues.reset();
            setTimeout(
                () => {
                    internalGoBack();
                    navigate(
                        'BookingSharingSelected',
                        params.type === 'availability'
                            ? {
                                  type: params.type,
                                  availabilityId: params.id,
                              }
                            : {
                                  type: params.type,
                                  requestId: params.id,
                              },
                    );
                },
                isWeb() ? 50 : 0,
            );
        };
        track(`Pressed ${isCreatingNew ? 'create' : 'update'} parking sharing for ${objectType}`, {
            variant: props.variant,
        });

        if (props.variant === 'edit-availability') {
            updateAvailability([props.editableAvailability.id, data], {
                onSuccess: (result) =>
                    getOnSuccess({ type: 'availability', id: result.parking_sharing_availability_id }),
                onError,
            });
            return;
        } else if (props.variant === 'new-availability' || props.variant === 'new-availability-from-request') {
            createAvailability(data, {
                onSuccess: (result) =>
                    getOnSuccess({ type: 'availability', id: result.parking_sharing_availability_id }),
                onError,
            });
            return;
        } else if (props.variant === 'edit-request') {
            updateRequest([props.editableRequest.id, data], {
                onSuccess: (result) => getOnSuccess({ type: 'request', id: result.parking_sharing_request_id }),
                onError,
            });
            return;
        } else if (props.variant === 'new-request' || props.variant === 'new-request-from-availability') {
            createRequest(
                {
                    ...data,
                    requested_availability_slot_id:
                        props.variant === 'new-request-from-availability' ? props.respondingToAvailability.id : null,
                },
                {
                    onSuccess: (result) => getOnSuccess({ type: 'request', id: result.parking_sharing_request_id }),
                    onError,
                },
            );
            return;
        }
    };

    const keyboardIsVisible = useKeyboardIsVisible();
    return (
        <SafeAreaView
            edges={['bottom', 'left', 'right']}
            style={{
                flex: 1,
                backgroundColor: theme.mainBackground,
                paddingBottom: keyboardIsVisible ? screenMargin : 0,
            }}
        >
            <HeaderWithNav title={t(`bookingSharing:${isCreatingNew ? 'new' : 'edit'}_${objectType}`)} />
            <ScrollView
                bounces={false}
                contentContainerStyle={{
                    paddingHorizontal: screenMargin,
                    flexGrow: 1,
                    gap: screenMargin,
                    paddingBottom: screenMargin,
                }}
            >
                <FormProvider {...formValues}>
                    {props.variant === 'new-availability-from-request' ? (
                        <RespondToRequest request={props.respondingToRequest} />
                    ) : (
                        <Create {...props} />
                    )}
                    {props.variant === 'edit-availability' || props.variant === 'edit-request' ? (
                        <UpdateWarning {...props} />
                    ) : null}
                    {props.variant === 'new-availability' ? <OverlappingAvailabilityWarning /> : null}
                </FormProvider>
            </ScrollView>
            <PrimaryButton
                style={{ marginHorizontal: screenMargin }}
                text={t(`bookingSharing:${isCreatingNew ? 'create' : 'update'}`)}
                onPress={formValues.handleSubmit(handleSubmit, handleInvalidSubmit(t))}
                status={
                    !formValues.formState.isValid
                        ? 'disabled'
                        : isCreatingRequest || isCreatingAvailability || isUpdatingRequest || isUpdatingAvailability
                          ? 'loading'
                          : null
                }
            />
            {content}
        </SafeAreaView>
    );
};

const RespondToRequest = ({ request }: { request: BookingSharingRequest }) => {
    const { t } = useTranslation();

    return (
        <>
            <BookingSharingInfo variant="request" {...request} />
            <View style={{ flex: 1 }} />
            <HeimeText variant="subtitle">{t('bookingSharing:request_response_explainer')}</HeimeText>
            <ContentInput variant="request" />
        </>
    );
};

const BookingSharingInfo = ({
    variant,
    creator_id,
    ...props
}: ComponentProps<typeof BookingSharingDetails> & {
    variant: 'request' | 'availability';
} & Pick<BookingSharingRequest | BookingSharingAvailability, 'creator_id'>) => {
    const { theme } = useThemeContext();

    // This is to sync so times do not argue with the api
    useTimeRangeSettings({ startName: 'start_at', endName: 'end_at', defaultStart: new Date() });
    return (
        <View
            style={{
                borderWidth: 1,
                borderColor: theme.main,
                padding: smallestMargin,
                gap: smallestMargin,
            }}
        >
            <View
                style={{
                    alignItems: 'center',
                    flexDirection: 'row',
                    gap: smallestMargin,
                    justifyContent: 'space-between',
                }}
            >
                <UserListItem
                    id={creator_id}
                    avatarStyle={{ width: WW * 0.07, height: WW * 0.07, borderRadius: (WW * 0.07) / 2 }}
                    containerStyle={{ paddingTop: 0, paddingBottom: 0, flex: 1 }}
                    noBottomBorder
                />
                <BookingSharingTag type={variant} />
            </View>
            <BookingSharingDetails {...props} />
        </View>
    );
};

const Create = (props: Exclude<DataVariants, { variant: 'new-availability-from-request' }>) => {
    const { t } = useTranslation();
    const { start_at, end_at } = useTimeRangeSettings({
        startName: 'start_at',
        endName: 'end_at',
        defaultStart: new Date(),
    });
    const objectType = getObjectType(props.variant);
    const isCreatingNew = getIsCreatingNew(props.variant);

    return (
        <>
            <HeimeText style={{ textAlign: 'left' }} variant="title">
                {t(`bookingSharing:${isCreatingNew ? 'new' : 'edit'}_${objectType}_title`)}
            </HeimeText>
            {props.variant === 'new-request-from-availability' ? (
                <BookingSharingInfo variant="availability" {...props.respondingToAvailability} />
            ) : null}
            {objectType === 'availability' && isCreatingNew ? (
                <HeimeText variant="subtitle">{t('bookingSharing:availability_explainer')}</HeimeText>
            ) : null}
            <ContentInput variant={objectType} />
            <DateInput label={t('bookingSharing:from_label')} {...start_at} />
            <DateInput label={t('bookingSharing:to_label')} {...end_at} />
            <CooperativeGroupDropdown isCreatingNew={isCreatingNew} />
        </>
    );
};

const DateInput = ({ label, ...props }: Omit<ComponentProps<typeof FormDateTimeInput>, 'mode' | 'chipStyle'>) => (
    <View style={{ flexDirection: 'row', gap: smallestMargin }}>
        <FormDateTimeInput label={label} chipStyle={{ marginLeft: smallestMargin }} mode="datetime" {...props} />
        <FormDateTimeInput chipStyle={{ marginLeft: 0 }} mode="time" {...props} />
    </View>
);

const ContentInput = ({ variant }: { variant: 'request' | 'availability' }) => {
    const { t } = useTranslation();

    return (
        <FormInput
            name="content"
            multiline
            numberOfLines={3}
            label={t(`bookingSharing:${variant}_content_label`)}
            placeholder={t(`bookingSharing:${variant}_content_placeholder`)}
        />
    );
};

const UpdateWarning = (
    props: Exclude<
        DataVariants,
        {
            variant:
                | 'new-request'
                | 'new-availability'
                | 'new-request-from-availability'
                | 'new-availability-from-request';
        }
    >,
) => {
    const { t } = useTranslation();
    const { watch } = useFormContext<FormValues>();
    const currentFrom = watch('start_at');
    const currentTo = watch('end_at');

    const assignedAvailability = useGetAssignedParkingAvailability(
        props.variant === 'edit-request'
            ? props.editableRequest.status === 'assigned'
                ? props.editableRequest.assigned_availability_id
                : null
            : null,
    );

    const message = useMemo(() => {
        const currentFromMoment = moment(currentFrom);
        const currentToMoment = moment(currentTo);

        // Case 1: Editing a request - check if it still fits in the availability
        if (props.variant === 'edit-request' && assignedAvailability) {
            // When editing a request, we need to check if the new request times still fit within the availability
            // Check if the request times are outside the availability range
            if (
                moment.max(moment(), currentFromMoment).isBefore(moment(assignedAvailability.start_at), 'minute') ||
                currentToMoment.isAfter(moment(assignedAvailability.end_at), 'minute')
            ) {
                return {
                    title: t('bookingSharing:request_no_longer_fits_availability'),
                    message: t('bookingSharing:request_no_longer_fits_availability_message'),
                };
            }
            return false;
        } else if (props.variant === 'edit-availability') {
            // Case 2: Editing an availability - check if the requests still fits
            // When editing an availability, we need to check if the new availability times still accommodate the requests
            const noLongerFits = props.assignedRequests.filter((request) => {
                const requestFrom = moment.max(moment(request.start_at), moment());
                const requestTo = moment(request.end_at);
                return (
                    currentFromMoment.isAfter(moment.max(requestFrom, moment()), 'minute') ||
                    currentToMoment.isBefore(requestTo, 'minute')
                );
            });

            if (noLongerFits.length > 0) {
                return {
                    title: t('bookingSharing:availability_no_longer_fits_request', {
                        count: noLongerFits.length,
                    }),
                    message: t('bookingSharing:availability_no_longer_fits_request_message', {
                        count: noLongerFits.length,
                    }),
                };
            }
            return false;
        }
        return false;
    }, [currentFrom, currentTo, assignedAvailability, t, props]);

    if (!message) {
        return null;
    }

    return <WarningMessage title={message.title} message={message.message} />;
};

const OverlappingAvailabilityWarning = () => {
    const { t } = useTranslation();
    const { watch } = useFormContext<FormValues>();
    const ownId = useOwnProfile()?.data?.id ?? 0;
    const currentFrom = watch('start_at');
    const currentTo = watch('end_at');

    const { data: availabilities } = useGetParkingSharingAvailabilities();
    // TODO pagination fetch all here!

    const overlappingAvailabilities = useMemo(() => {
        const ownAvailabilities = flattenIniniteResult(availabilities).filter(
            (availability) => availability.creator_id === ownId,
        );

        const currentFromMoment = moment(currentFrom);
        const currentToMoment = moment(currentTo);

        return ownAvailabilities.filter((availability) =>
            isBookingInSlot(
                currentFromMoment.unix(),
                currentToMoment.unix(),
            )({
                start_at: moment(availability.start_at).unix(),
                end_at: moment(availability.end_at).unix(),
            }),
        );
    }, [availabilities, currentFrom, currentTo, ownId]);

    if (overlappingAvailabilities.length === 0) {
        return null;
    }

    return (
        <WarningMessage
            title={t('bookingSharing:overlapping_availability_warning_title', {
                count: overlappingAvailabilities.length,
            })}
            message={t('bookingSharing:overlapping_availability_warning_message', {
                count: overlappingAvailabilities.length,
            })}
        />
    );
};

const WarningMessage = ({ title, message }: { title: string; message: string }) => {
    const { theme } = useThemeContext();
    return (
        <>
            <View style={{ flex: 1 }} />
            <View
                style={{
                    borderWidth: 1,
                    borderColor: theme.error,
                    borderRadius: 8,
                    padding: smallestMargin,
                    backgroundColor: theme.white,
                }}
            >
                <View style={{ flexDirection: 'row', gap: smallestMargin, alignItems: 'center' }}>
                    <Icon name="alertCircle" color={'error'} />
                    <HeimeText variant="subtitle" style={{ color: theme.error, fontWeight: 'bold', flex: 1 }}>
                        {title}
                    </HeimeText>
                </View>
                <HeimeText variant="subtitle" style={{ color: theme.black, marginTop: 4 }}>
                    {message}
                </HeimeText>
            </View>
        </>
    );
};

export default BookingSharingNew;
